/**
 * SW2-2443 — push a downloaded project's offline work to the server.
 *
 * Moved out of InspectionsDue.jsx, where it ran as two near-copies (inspections
 * already on the server, and inspections created offline) that swallowed most
 * errors and then always reported success. The widget deleted the project's
 * local copy afterwards, so anything that had not uploaded — a connection
 * dropped mid-sync — was destroyed while the UI said "synced".
 *
 * The rules this module keeps:
 *
 *   - A queued write is marked sent only after its request succeeds. Rows that
 *     queue several writes (a finding's create + updates, a certification's
 *     signatures, inspection notes) drop each entry as it lands, so a retry
 *     sends only what is left — never a duplicate.
 *   - A network failure stops the sync. Nothing after it can succeed, and every
 *     request made anyway is one more partial write to reconcile.
 *   - Any other failure (a server rejection) leaves that item queued and moves
 *     on to the rest.
 *   - An inspection created offline is re-keyed to its server id, with
 *     everything recorded against it, before its work is sent. From then on it
 *     is an ordinary synced inspection, so a retry finds whatever is left.
 *
 * The caller decides what to do with the result: delete the local copy only
 * when `remaining` is 0. Sending is in ./offlineSyncSend, findings and their
 * photos in ./offlineFindingSync.
 */
import db, {
  countUnsyncedWork,
  isInspectionAttachment,
  rekeyOfflineInspection,
} from '../db/db';
import inspectionApi from './api/inspection';
import { certificationApi } from './api/certification';
import commentableApi from './api/commentable';
import { fileFromOfflineImage, hasReadableImageData, uploadApi } from './api/upload';
import { positionableApi } from './api/positionable';
import { syncFindings } from './offlineFindingSync';
import {
  ConnectionLost,
  FAILED,
  hasPendingCreate,
  isPending,
  send,
  takeSentEntries,
} from './offlineSyncSend';

export { ConnectionLost, FAILED } from './offlineSyncSend';

async function syncAttachments(inspectionId) {
  const pending = await db.images
    .where('payload_id')
    .equals(inspectionId)
    .filter((upload) => !upload.sync.synced && isInspectionAttachment(upload))
    .toArray();

  for (const upload of pending) {
    const label = `attachment ${upload.guid}`;
    // Two requests: upload the file, then link it to the inspection. The upload
    // id is recorded in between, so a link that fails is retried against the
    // same upload rather than uploading the file again.

    let uploadId = upload.sync.uploadId ?? null;

    if (!uploadId) {
      const file = fileFromOfflineImage(upload);

      if (!file) {
        console.error(
          `Skipping inspection attachment ${upload.guid}: no readable offline file data.`,
        );

        continue;
      }

      const uploaded = await send(label, () => uploadApi.create(file));

      if (uploaded === FAILED || !uploaded?.id) continue;

      uploadId = uploaded.id;
      await db.images.update(upload.guid, { 'sync.uploadId': uploadId });
    }

    const linked = await send(label, () =>
      uploadApi.inspection.create(uploadId, inspectionId),
    );

    if (linked === FAILED) continue;

    await db.images.update(upload.guid, { 'sync.synced': true });
  }
}

/**
 * Scoped by project, not start_inspection_id: that field records where a
 * positionable was originally CREATED, so moving or reshaping one from an
 * earlier inspection left it unmatched.
 *
 * Held back until the records it points at exist on the server: a placement of
 * a finding whose create has not landed, or one started on an inspection not
 * yet created, would reach the server carrying a local id.
 */
async function syncPositionables(projectId) {
  const inspections = await db.inspections
    .where('project_id')
    .equals(projectId)
    .toArray();
  const unsentInspections = new Set(
    inspections.filter(isPending).map((i) => i.id),
  );
  const findingIds = inspections
    .map((i) => (i.findings || []).map((f) => f.id))
    .flat();
  const unsentFindings = new Set(
    (await db.findings.bulkGet(findingIds))
      .filter(hasPendingCreate)
      .map((f) => f.id),
  );

  const pending = await db.positionables
    .where('project_id')
    .equals(projectId)
    .filter((p) => !p.sync.synced)
    .toArray();

  // Either end of the inspection window, on the row or in what it will send.
  const waitsOnInspection = (p) =>
    [p, ...(p.sync.payload || [])].some(
      (r) =>
        unsentInspections.has(r.start_inspection_id) ||
        unsentInspections.has(r.end_inspection_id),
    );

  for (const positionable of pending) {
    const type =
      positionable.positionable_type ||
      positionable.sync.payload?.[0]?.positionable_type;

    if (
      waitsOnInspection(positionable) ||
      (type === 'findings' && unsentFindings.has(positionable.positionable_id))
    ) {
      continue;
    }

    const saved = await send(`positionable ${positionable.id}`, () =>
      positionableApi.bulkUpdate(
        positionable.target_type || positionable.tType,
        positionable.target_id,
        positionable.sync.payload,
        { replay: true },
      ),
    );

    if (saved === FAILED) continue;

    // bulkUpdate mirrors the server's rows into db.positionables under their
    // server ids. A placement created offline has a local id the server never
    // saw, so keeping that row as well shows it twice on the offline map — which
    // now matters, because a partial sync keeps the local copy.
    if (
      Array.isArray(saved) &&
      saved.length &&
      !saved.some((row) => row.id === positionable.id)
    ) {
      // SW2-2446: a move saved while this request was in flight sits on the local row. Carry it,
      // and the row's latest fields, to the server's row rather than delete it with the local one.
      // With more than one row back it cannot be told which is this placement, so the local row
      // keeps that move instead.
      await db.transaction('rw', db.positionables, async () => {
        const current = await db.positionables.get(positionable.id);
        const rest = (current?.sync?.payload || []).slice(positionable.sync.payload.length);

        if (!rest.length) {
          await db.positionables.delete(positionable.id);

          return;
        }

        if (saved.length !== 1) {
          await db.positionables.update(positionable.id, { 'sync.payload': rest });

          return;
        }

        const serverId = saved[0].id;
        const serverRow = await db.positionables.get(serverId);
        const { id: _localId, sync: _localSync, ...latest } = current;

        await db.positionables.delete(positionable.id);
        await db.positionables.put({
          ...(serverRow || {}),
          ...latest,
          id: serverId,
          sync: {
            synced: false,
            payload: [
              ...(serverRow?.sync?.payload || []),
              ...rest.map((entry) =>
                entry.id === positionable.id ? { ...entry, id: serverId } : entry,
              ),
            ],
          },
        });
      });
    } else {
      // SW2-2446: take off only what was sent. Clearing the whole queue would also drop a move
      // saved while the request was in flight (PR #146 review); marking only `synced` left the
      // sent entries for the next save to send again.
      await db.transaction('rw', db.positionables, async () => {
        const current = await db.positionables.get(positionable.id);

        if (!current) return;

        const rest = (current.sync?.payload || []).slice(positionable.sync.payload.length);

        await db.positionables.update(positionable.id, {
          'sync.synced': rest.length === 0,
          'sync.payload': rest,
        });
      });
    }
  }
}

/**
 * SW2-2459 — take a signature the server refused off the device: out of the queue, and off its
 * line, so the line can be signed again. Kept, it stayed "waiting to upload" for good, and the
 * project never counted as synced, so every later signature on it was queued too (PR #156 review).
 */
/** The inspection's date a complete signature group sets (certification.ts `createOfflineCertification`). */
const COMPLETION_DATE = {
  inspection: 'certification_date',
  acknowledgement: 'acknowledgement_date',
  compliance: 'compliance_date',
};

const dropRefusedSignature = (inspectionId, entry) =>
  db.transaction('rw', db.certifications, db.inspections, async () => {
    const row = await db.certifications.get(inspectionId);

    if (!row) return [];

    // It completed its group on the device: the inspection goes back to how it was before it.
    if (entry.completes) {
      const { field, date, status } = entry.completes;

      await db.inspections.update(inspectionId, {
        [field]: date,
        ...(status !== undefined ? { status } : {}),
      });
    } else if (
      (row.userSignatures?.[entry.group] || []).length &&
      (row.userSignatures?.[entry.group] || []).length === (row.signatureGroups?.[entry.group] || []).length
    ) {
      // Queued before entries recorded that (an earlier release): the group was complete, so this
      // signature completed it. Without the old values, clear the date; an inspection group goes
      // back to "answered", the state signing it needs (PR #156 review).
      await db.inspections.update(inspectionId, {
        [COMPLETION_DATE[entry.group] || 'certification_date']: null,
        ...(entry.group === 'inspection' ? { status: 'answered' } : {}),
      });
    }

    const rest = (row.sync?.payload || []).filter((p) => p.id !== entry.id);
    const userSignatures = {
      ...(row.userSignatures || {}),
      [entry.group]: (row.userSignatures?.[entry.group] || []).filter((s) => s.id !== entry.id),
    };

    await db.certifications.update(inspectionId, {
      userSignatures,
      'sync.payload': rest,
      'sync.synced': rest.length === 0,
    });

    return rest;
  });

/**
 * The server answered and won't take the signature as it is: invalid (400 — the backend's
 * signature validation, e.g. dated before the floor), conflicting (409) or unprocessable (422).
 * Anything else may go through later — an expired session (401), a refused permission that may be
 * restored (403), rate limiting (429), a server error (5xx) — so the signature stays queued.
 */
const REFUSED_STATUSES = [400, 409, 422];
const isRefusal = (err) => REFUSED_STATUSES.includes(err?.response?.status);

/**
 * The server already has this line signed (signatureValidationService's duplicate check). After a
 * request whose answer was lost (a timeout, SW2-2458) the retry gets this although the first one
 * landed: it is sent, not refused — dropping it told the user to sign again and rolled the
 * inspection back after its report had gone out (PR #156 review).
 */
const isAlreadySubmitted = (err) => /has already been submitted/i.test(err?.response?.data?.message || '');

/**
 * @returns {Promise<Array<{ inspectionId: number, group: string, lineType: string, reviewRaised?: true }>>} sent.
 * Signatures the server refused are added to `refused`, with its reason.
 */
async function syncCertification(inspectionId, refused = []) {
  const certification = await db.certifications.get(inspectionId);
  const sent = [];

  if (!isPending(certification)) return sent;

  let queue = [...(certification.sync.payload || [])];

  while (queue.length) {
    const entry = queue[0];
    // SW2-2459: the queued `resend` flag is the call's second argument, not part of the data.
    // Passed inside the data it was dropped, so a Resend queued offline replayed as a first
    // signature, was refused ("already submitted"), and stayed queued for good.
    // `completes` is the device's own record (what to undo on a refusal), not for the server.
    // eslint-disable-next-line no-unused-vars
    const { resend, completes, ...data } = entry;

    let error = null;

    const result = await send(`certification for inspection ${inspectionId}`, () =>
      certificationApi
        .create({ ...data, inspectionId }, resend, {
          replay: true,
        })
        .catch((err) => {
          error = err;
          throw err;
        }),
    );

    if (result === FAILED && !isAlreadySubmitted(error)) {
      // Marked synced only once every signature has landed. Before SW2-2443 it
      // was marked synced even when the request failed, so the signature was
      // never retried. Still so for a server error: it may go through next time.
      if (!isRefusal(error)) return sent;

      refused.push({
        inspectionId,
        group: entry.group,
        lineType: entry.lineType,
        reason: error?.response?.data?.message || error?.message || 'refused by the server',
      });
      queue = await dropRefusedSignature(inspectionId, entry);

      continue;
    }

    // SW2-2459 × SW2-2248: the server's answer says whether this signature raised a review instead
    // of sending the report (backend #479; the old body was empty). Unknown after "already
    // submitted" — the first attempt's answer was lost — so not claimed then.
    const reviewRaised = result !== FAILED && result?.data?.reviewRaised === true;

    sent.push({
      inspectionId,
      group: entry.group,
      lineType: entry.lineType,
      ...(reviewRaised ? { reviewRaised: true } : {}),
    });
    queue = await takeSentEntries(db.certifications, inspectionId, 1);
  }

  await takeSentEntries(db.certifications, inspectionId, 0);

  return sent;
}

async function syncNotes(inspectionId) {
  const notes = await db.inspectionNotes.get(inspectionId);

  if (!isPending(notes)) return;

  // Before SW2-2443 the first note on an inspection was queued with no id
  // (commentable.js), so a later edit of it could not be matched to the server
  // note. That note is always the row's first; give queues from earlier
  // releases its id.
  const withIds = (payload) =>
    payload.map((note) =>
      note.method === 'create' && note.id === undefined
        ? { ...note, id: notes.notes?.[0]?.id }
        : note,
    );

  let queue = withIds(notes.sync.payload || []);
  // Local note id → server id, kept on the row so an update queued behind a
  // create still finds its note when a retry resumes between the two.
  const noteIds = { ...(notes.sync.noteIds || {}) };

  while (queue.length) {
    const note = queue[0];
    const label = `note for inspection ${inspectionId}`;

    if (note.method === 'create') {
      const created = await send(label, () =>
        commentableApi.create(
          'inspections',
          inspectionId,
          { note: { content: note.content } },
          { replay: true },
        ),
      );

      if (created === FAILED) return;

      noteIds[note.id] = created?.id;
    } else if (note.method === 'update') {
      const updated = await send(label, () =>
        commentableApi.update(
          'inspections',
          note.id,
          { content: note.content, id: noteIds[note.id] || note.id },
          inspectionId,
          { replay: true },
        ),
      );

      if (updated === FAILED) return;
    } else {
      console.error(`Offline sync: ${label} has a queued "${note.method}" it cannot send`);

      return;
    }

    queue = withIds(
      await takeSentEntries(db.inspectionNotes, inspectionId, 1, { 'sync.noteIds': noteIds }),
    );
  }

  await takeSentEntries(db.inspectionNotes, inspectionId, 0);
}

async function syncQuestionComments(inspectionId) {
  const pending = await db.commentPayload
    .where('inspection_id')
    .equals(inspectionId)
    .filter((comment) => !comment.sync.synced)
    .toArray();

  // One row per comment, marked as each lands — they used to be marked together
  // after the loop, so one failure resent every comment before it.
  for (const comment of pending) {
    const sent = await send(`comment on question ${comment.question_id}`, () =>
      inspectionApi.createQuestionComment(
        comment.question_id,
        inspectionId,
        comment.sync.payload,
        { replay: true },
      ),
    );

    if (sent !== FAILED) {
      await db.commentPayload.update(comment.id, { 'sync.synced': true });
    }
  }
}

async function syncAnswers(inspectionId) {
  const question = await db.questions.get(inspectionId);

  if (!isPending(question)) return;

  const sent = await send(`answers for inspection ${inspectionId}`, () =>
    inspectionApi.update(inspectionId, question.sync.payload, { replay: true }),
  );

  if (sent === FAILED) return;

  // SW2-2446: synced only if the answers are still the ones sent. A save while the request was in
  // flight merged newer answers into the set; marking it synced hid them.
  await db.transaction('rw', db.questions, async () => {
    const current = await db.questions.get(inspectionId);

    if (current && JSON.stringify(current.sync?.payload) === JSON.stringify(question.sync.payload)) {
      await db.questions.update(inspectionId, { 'sync.synced': true });
    }
  });
}

/**
 * SW2-2459 — is anything for this inspection still queued, other than its signatures?
 *
 * A signature is what makes the server build and email the Inspection Complete report, from what
 * the server holds at that moment. Sending it while the inspection's findings, photos, answers,
 * notes, comments or the project's map placements are still on the device emails a report without
 * them, so it
 * waits for them. A photo or attachment with no readable data can never be sent (it is skipped and
 * stays queued); it does not hold the signature, or the report would never go.
 */
async function inspectionHasUnsentWork(inspectionId, projectId) {
  const inspection = await db.inspections.get(inspectionId);
  const listedFindingIds = (inspection?.findings || []).map((f) => f.id);
  const [question, notes, uploads, comments, byInspection, listed, placements] = await Promise.all([
    db.questions.get(inspectionId),
    db.inspectionNotes.get(inspectionId),
    db.inspectionUploads.get(inspectionId),
    db.commentPayload.where('inspection_id').equals(inspectionId).toArray(),
    db.findings.where('inspection_id').equals(inspectionId).toArray(),
    db.findings.bulkGet(listedFindingIds),
    db.positionables.where('project_id').equals(projectId).toArray(),
  ]);
  const findings = [...byInspection, ...listed].filter(Boolean);
  const findingIds = new Set(findings.map((f) => f.id));
  const images = (
    await db.images
      .where('payload_id')
      .anyOf([inspectionId, ...findingIds])
      .toArray()
  ).filter(
    (img) =>
      (isInspectionAttachment(img)
        ? img.payload_id === inspectionId
        : findingIds.has(img.payload_id)) &&
      // Readable data: the same test the upload applies, so a photo it would skip never holds the
      // signature (PR #150 review).
      (img.sync?.uploadId || hasReadableImageData(img)),
  );
  // Every pending placement of the project, not just the ones this inspection started: a
  // placement from an earlier inspection can be active and edited in this one, and its window
  // (start/end inspection) can itself be what is queued (PR #150 review).

  return [question, notes, uploads, ...comments, ...findings, ...images, ...placements].some(
    isPending,
  );
}

/**
 * Send everything queued for one inspection that exists on the server: answers, findings,
 * attachments, map placements, notes and comments — then its signatures, and only once all of
 * that is on the server (SW2-2459). Anything refused or interrupted keeps the signatures queued,
 * so the report they trigger is never sent ahead of the work it reports.
 */
async function syncInspectionWork(inspectionId, projectId, onStatus, sentSignatures = [], refusedSignatures = []) {
  const inspection = await db.inspections.get(inspectionId);

  onStatus('Syncing answers and findings...');
  await syncAnswers(inspectionId);
  await syncFindings(inspection);

  onStatus('Uploading attachments and map placements...');
  await syncAttachments(inspectionId);
  await syncPositionables(projectId);

  onStatus('Syncing notes and comments...');
  await syncNotes(inspectionId);
  await syncQuestionComments(inspectionId);

  if (!isPending(await db.certifications.get(inspectionId))) return false;

  if (await inspectionHasUnsentWork(inspectionId, projectId)) {
    // eslint-disable-next-line no-console
    console.warn(`Offline sync: holding the signature for inspection ${inspectionId} until its other work is sent`);

    return true;
  }

  onStatus('Sending signatures...');
  sentSignatures.push(...(await syncCertification(inspectionId, refusedSignatures)));

  return false;
}

/**
 * @returns {Promise<{ remaining: number, interrupted: boolean, heldSignatures: number[] }>}
 *   `remaining` — rows of this project still not on the server.
 *   `interrupted` — the sync stopped early because the server was unreachable.
 *   `heldSignatures` — inspections whose queued signature was held back because their other work
 *   did not all reach the server (SW2-2459): their Inspection Complete report has not been sent.
 *   `sentSignatures` — queued signatures this run sent (`{ inspectionId, group, lineType }`).
 *   `refusedSignatures` — queued signatures the server refused, taken off the device so they can be
 *   signed again (`{ inspectionId, group, lineType, reason }`).
 *   Remaining work with `interrupted` false is work the server refused.
 */
export async function syncOfflineProject(
  projectId,
  { onStatus = () => {}, onProgress = () => {} } = {},
) {
  const project = await db.projects.get(projectId);

  if (!project) throw new Error('Project not found');

  onStatus(`Syncing project ${project.name}...`);

  let interrupted = false;
  const heldSignatures = [];
  const sentSignatures = [];
  const refusedSignatures = [];

  try {
    const inspections = await db.inspections
      .where('project_id')
      .equals(projectId)
      .toArray();

    for (const [index, inspection] of inspections.entries()) {
      onStatus(`Syncing inspection ${index + 1}/${inspections.length}...`);

      let inspectionId = inspection.id;

      if (isPending(inspection)) {
        // Recorded as soon as the create lands, so a re-key that fails (an id
        // already taken on this device) is retried against the same server
        // inspection rather than creating another.
        let serverId = inspection.sync.serverId ?? null;

        if (!serverId) {
          const { projectId: payloadProjectId, inspection_types } =
            inspection.sync.payload || {};
          const created = await send(`inspection ${inspection.id}`, () =>
            inspectionApi.create(payloadProjectId, inspection_types, 'offline', {
              replay: true,
            }),
          );

          // Nothing recorded against it can be sent until it exists.
          if (created === FAILED || !created?.id) continue;

          serverId = created.id;
          await db.inspections.update(inspection.id, { 'sync.serverId': serverId });
        }

        await rekeyOfflineInspection(inspection.id, serverId);
        inspectionId = serverId;
      }

      if (await syncInspectionWork(inspectionId, projectId, onStatus, sentSignatures, refusedSignatures)) {
        heldSignatures.push(inspectionId);
      }
      onProgress(Math.round(((index + 1) / inspections.length) * 95));
    }

    // Placements held back until a later inspection or finding was created.
    await syncPositionables(projectId);
  } catch (err) {
    if (!(err instanceof ConnectionLost)) throw err;

    console.error(err.message, err.cause);
    interrupted = true;
  }

  return {
    remaining: await countUnsyncedWork(projectId),
    interrupted,
    heldSignatures,
    sentSignatures,
    refusedSignatures,
  };
}
